import { useEffect, useMemo, useState } from 'react';
import { mindtimeWazuhEndpointsApi } from 'src/api/mindtime/wazuhEndpointsApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type { WazuhPagedData } from 'src/types/mindtime/wazuhDashboard';
import type { WazuhEndpointItem, WazuhEndpointsSummaryData } from 'src/types/mindtime/wazuhEndpoints';
import logger from 'src/utils/logger';
import { buildEndpointsApiScope } from '../shared/wazuhScopeParams';

export type EndpointsFilters = {
  pageOrganizationId?: string;
  selectedEndpointId?: string;
  system?: string;
  status?: string;
  agentStatus?: string;
  riskLevel?: string;
};

export type EndpointsSortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type EndpointsState = {
  loading: boolean;
  summary: WazuhEndpointsSummaryData | null;
  items: WazuhPagedData<WazuhEndpointItem> | null;
};

const emptyState = (): EndpointsState => ({
  loading: true,
  summary: null,
  items: null,
});

export const useEndpointsData = (
  page = 0,
  pageSize = 15,
  search = '',
  filters: EndpointsFilters = {},
  refreshKey = 0,
  sortParams: EndpointsSortParams | null = null
): EndpointsState => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'endpoints');
  const canTable = canWidget('table');
  const canAgentOverview = canWidget('agentOverview');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<EndpointsState>(emptyState);

  const { pageOrganizationId, system, status, agentStatus, riskLevel } = filters;
  const resolvedEndpointId = selectedEndpointId?.trim() || filters.selectedEndpointId || '';

  const listScope = useMemo(
    () =>
      buildEndpointsApiScope({
        scope,
        profileOrgId,
        isReseller,
        pageOrganizationId,
      }),
    [scope, profileOrgId, isReseller, pageOrganizationId]
  );

  const endpointScopedParams = useMemo(
    () => ({
      ...listScope,
      ...(resolvedEndpointId ? { wazuhAgentId: resolvedEndpointId } : {}),
    }),
    [listScope, resolvedEndpointId]
  );

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as EndpointsState);
      return undefined;
    }

    if (!canAgentOverview) {
      setState((prev) => ({ ...prev, summary: null }));
      return undefined;
    }

    let cancelled = false;

    mindtimeWazuhEndpointsApi
      .getSummary(endpointScopedParams)
      .then((summary) => {
        if (!cancelled) setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useEndpointsData.summary');
          setState((prev) => ({ ...prev, summary: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isPreviewMode, endpointScopedParams, refreshKey, wait, getPreviewState, canAgentOverview]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canTable) {
      setState((prev) => ({ ...prev, loading: false, items: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    const listParams = {
      ...endpointScopedParams,
      page,
      pageSize,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(system ? { system } : {}),
      ...(status ? { status } : {}),
      ...(agentStatus ? { agentStatus } : {}),
      ...(riskLevel ? { riskLevel } : {}),
      ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
    };

    mindtimeWazuhEndpointsApi
      .list(listParams)
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, loading: false, items }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useEndpointsData');
          setState((prev) => ({ ...prev, loading: false, items: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    isPreviewMode,
    skipLive,
    endpointScopedParams,
    page,
    pageSize,
    search,
    system,
    status,
    agentStatus,
    riskLevel,
    refreshKey,
    sortParams?.column,
    sortParams?.sort,
    canTable,
  ]);

  return state;
};
